// [S-704 / E-PERSONA-ADMIN] Persona configuration admin data hooks, over
// `/platform/personas/*` (apps/admin/src/routes/personas.ts). Mirrors
// classificationQuery.ts's conventions. Reuses ChatAgentKind from chatQuery —
// same AgentKind enum, one type instead of a duplicate.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";
import type { ChatAgentKind } from "./chatQuery";

const BASE = "/platform/personas";

export const personaConfigListKey = ["personaConfig", "list"] as const;
export const personaConfigKey = (kind: ChatAgentKind) =>
  ["personaConfig", kind] as const;

// ─── wire types — mirror what the admin route emits ─────────────────────

export type PersonaOverrideFieldsWire = {
  additionalInstructions: string | null;
  allowedToolsOverride: string[] | null;
  modelOverride: string | null;
  maxStepsOverride: number | null;
  maxToolCallsOverride: number | null;
  maxTokensOverride: number | null;
};

export type PersonaOverrideDocWire = {
  kind: ChatAgentKind;
  draft: PersonaOverrideFieldsWire | null;
  published: PersonaOverrideFieldsWire | null;
  publishedAt: string | null;
  publishedBy: string | null;
  updatedAt: string;
  updatedBy: string;
  createdAt: string;
};

export type EffectivePersonaWire = {
  kind: ChatAgentKind;
  label: string;
  blurb: string;
  systemPrompt: string;
  allowedTools: string[];
  model?: string;
  maxSteps: number;
  maxToolCalls: number;
  maxTokens: number;
};

export type PersonaConfigListItemWire = {
  kind: ChatAgentKind;
  label: string;
  blurb: string;
  hasDraft: boolean;
  hasPublished: boolean;
  publishedAt: string | null;
  workflowBound: boolean;
};

export type PersonaConfigDetailWire = {
  kind: ChatAgentKind;
  /** The pure code default (the persona manifest), unmerged. */
  default: EffectivePersonaWire;
  /** Code default + published override, what live chat actually uses. */
  effective: EffectivePersonaWire;
  override: PersonaOverrideDocWire | null;
  toolCatalog: string[];
  pinnedTools: string[];
  supportedModels: string[];
  workflowSteps: WorkflowStepWire[] | null;
};

export type WorkflowStepWire = {
  name: string;
  description: string;
  tool: string | null;
};

export const EMPTY_PERSONA_OVERRIDE_FIELDS: PersonaOverrideFieldsWire = {
  additionalInstructions: null,
  allowedToolsOverride: null,
  modelOverride: null,
  maxStepsOverride: null,
  maxToolCallsOverride: null,
  maxTokensOverride: null,
};

// ─── reads ────────────────────────────────────────────────────────────

export function usePersonaConfigs(): UseQueryResult<
  { personas: PersonaConfigListItemWire[] },
  Error
> {
  const api = useApi("admin");
  return useQuery({
    queryKey: personaConfigListKey,
    queryFn: () => api<{ personas: PersonaConfigListItemWire[] }>(BASE),
  });
}

export function usePersonaConfig(
  kind: ChatAgentKind
): UseQueryResult<PersonaConfigDetailWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: personaConfigKey(kind),
    queryFn: () => api<PersonaConfigDetailWire>(`${BASE}/${kind}`),
  });
}

// ─── writes ───────────────────────────────────────────────────────────

function useInvalidatePersona() {
  const qc = useQueryClient();
  return (kind: ChatAgentKind) => {
    void qc.invalidateQueries({ queryKey: personaConfigKey(kind) });
    void qc.invalidateQueries({ queryKey: personaConfigListKey });
  };
}

export function useSavePersonaDraft(): UseMutationResult<
  PersonaOverrideDocWire,
  Error,
  { kind: ChatAgentKind; fields: Partial<PersonaOverrideFieldsWire> }
> {
  const api = useApi("admin");
  const invalidate = useInvalidatePersona();
  return useMutation({
    mutationFn: ({ kind, fields }) =>
      api<PersonaOverrideDocWire>(`${BASE}/${kind}/draft`, {
        method: "PATCH",
        body: fields,
      }),
    onSuccess: (_doc, vars) => invalidate(vars.kind),
  });
}

export function useDiscardPersonaDraft(): UseMutationResult<
  { kind: ChatAgentKind; override: PersonaOverrideDocWire | null },
  Error,
  ChatAgentKind
> {
  const api = useApi("admin");
  const invalidate = useInvalidatePersona();
  return useMutation({
    mutationFn: (kind) =>
      api<{ kind: ChatAgentKind; override: PersonaOverrideDocWire | null }>(
        `${BASE}/${kind}/draft`,
        { method: "DELETE" }
      ),
    onSuccess: (_res, kind) => invalidate(kind),
  });
}

export function usePublishPersonaConfig(): UseMutationResult<
  PersonaOverrideDocWire,
  Error,
  ChatAgentKind
> {
  const api = useApi("admin");
  const invalidate = useInvalidatePersona();
  return useMutation({
    mutationFn: (kind) =>
      api<PersonaOverrideDocWire>(`${BASE}/${kind}/publish`, { method: "POST" }),
    onSuccess: (_doc, kind) => invalidate(kind),
  });
}

export function useRevertPersonaConfig(): UseMutationResult<
  { kind: ChatAgentKind; override: PersonaOverrideDocWire | null },
  Error,
  ChatAgentKind
> {
  const api = useApi("admin");
  const invalidate = useInvalidatePersona();
  return useMutation({
    mutationFn: (kind) =>
      api<{ kind: ChatAgentKind; override: PersonaOverrideDocWire | null }>(
        `${BASE}/${kind}/revert`,
        { method: "POST" }
      ),
    onSuccess: (_res, kind) => invalidate(kind),
  });
}
